import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Loader2, Trash2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../../components/ui/dialog';
import { toast } from 'sonner';
import { operacionesApi, unidadesApi } from '../../../lib/api';

export default function OperacionesLimites({ edificioId }) {
  const [limites, setLimites] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ tipo: 'unidad', target_id: '', target_nombre: '', horas_semanales: 4 });
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [l, u, us] = await Promise.all([
        operacionesApi.listLimites(edificioId),
        unidadesApi.list({ edificio_id: edificioId }),
        operacionesApi.listUsuariosEdificio({ edificio_id: edificioId }),
      ]);
      setLimites(l.data || []);
      setUnidades((u.data || []).filter(x => x.edificio_id === edificioId));
      setUsers(us.data || []);
    } catch { toast.error('Error al cargar'); }
    finally { setLoading(false); }
  }, [edificioId]);

  useEffect(() => { reload(); }, [reload]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.target_id) { toast.error('Seleccioná un target'); return; }
    setSaving(true);
    try {
      let target_nombre = form.target_nombre;
      if (form.tipo === 'unidad') {
        target_nombre = unidades.find(x => x.unidad_id === form.target_id)?.nombre_unidad || form.target_id;
      } else if (form.tipo === 'persona') {
        const usr = users.find(x => x.usuario_edif_id === form.target_id);
        target_nombre = usr ? `${usr.nombre} ${usr.apellido}` : form.target_id;
      }
      await operacionesApi.createLimite({
        edificio_id: edificioId, tipo: form.tipo, target_id: form.target_id,
        target_nombre, horas_semanales: parseFloat(form.horas_semanales) || 0,
      });
      toast.success('Límite guardado');
      setOpen(false);
      setForm({ tipo: 'unidad', target_id: '', target_nombre: '', horas_semanales: 4 });
      reload();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  const del = async (l) => {
    if (!confirm(`¿Eliminar límite de ${l.target_nombre}?`)) return;
    try {
      await operacionesApi.deleteLimite(l.limite_id);
      toast.success('Eliminado');
      reload();
    } catch { toast.error('Error'); }
  };

  if (loading) return <div className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400 inline" /></div>;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-xs text-slate-500">Límites de horas semanales de reserva. Los de "unidad" suman a todos sus usuarios.</div>
        <Button size="sm" onClick={() => setOpen(true)} data-testid="btn-new-limite">
          <Plus className="w-3.5 h-3.5 mr-1" /> Nuevo límite
        </Button>
      </div>
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="text-left px-3 py-2 font-medium">Tipo</th>
              <th className="text-left px-3 py-2 font-medium">Target</th>
              <th className="text-right px-3 py-2 font-medium">Horas/semana</th>
              <th></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {limites.length === 0 && <tr><td colSpan={4} className="text-center text-slate-400 py-8">Sin límites configurados</td></tr>}
            {limites.map(l => (
              <tr key={l.limite_id}>
                <td className="px-3 py-2 text-xs uppercase text-slate-600">{l.tipo}</td>
                <td className="px-3 py-2">{l.target_nombre}</td>
                <td className="px-3 py-2 text-right font-mono">{l.horas_semanales}</td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => del(l)} className="p-1 hover:bg-red-50 rounded"><Trash2 className="w-3.5 h-3.5 text-red-500" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <form onSubmit={submit}>
            <DialogHeader><DialogTitle>Nuevo límite de reserva</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <Label>Tipo de límite</Label>
                <select className="w-full border border-slate-200 rounded h-9 px-2"
                  value={form.tipo}
                  onChange={e => setForm({ ...form, tipo: e.target.value, target_id: '' })}>
                  <option value="unidad">Por unidad (suma todos sus usuarios)</option>
                  <option value="persona">Por persona individual</option>
                  <option value="plan">Por plan / categoría</option>
                </select>
              </div>
              {form.tipo === 'unidad' && (
                <div>
                  <Label>Unidad</Label>
                  <select className="w-full border border-slate-200 rounded h-9 px-2"
                    value={form.target_id}
                    onChange={e => setForm({ ...form, target_id: e.target.value })}>
                    <option value="">— Elegir —</option>
                    {unidades.map(u => <option key={u.unidad_id} value={u.unidad_id}>{u.nombre_unidad}</option>)}
                  </select>
                </div>
              )}
              {form.tipo === 'persona' && (
                <div>
                  <Label>Persona</Label>
                  <select className="w-full border border-slate-200 rounded h-9 px-2"
                    value={form.target_id}
                    onChange={e => setForm({ ...form, target_id: e.target.value })}>
                    <option value="">— Elegir —</option>
                    {users.map(u => <option key={u.usuario_edif_id} value={u.usuario_edif_id}>{u.nombre} {u.apellido} ({u.email})</option>)}
                  </select>
                </div>
              )}
              {form.tipo === 'plan' && (
                <div>
                  <Label>Nombre del plan</Label>
                  <Input value={form.target_id} onChange={e => setForm({ ...form, target_id: e.target.value, target_nombre: e.target.value })} placeholder="Básico / Premium / Custom" />
                </div>
              )}
              <div>
                <Label>Horas semanales</Label>
                <Input type="number" min={0} step={0.5} value={form.horas_semanales} onChange={e => setForm({ ...form, horas_semanales: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving}>{saving ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Guardar'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
